import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/common/common-components/ui/Card";
import { Input } from "@/app/common/common-components/ui/Input";
import { Label } from "@/app/common/common-components/ui/Label";
import { formatNumber, formatInputNumber } from "@/app/common/common-functions/functions";

interface TransferCostCalculatorProps {
  countryCode?: string;
  currencySymbol?: string;
}

const registrationFeeBrackets = [
  { from: 0, to: 100000, baseCost: 0, interval: 100000, intervalCost: 6435 },
  { from: 100000, to: 500000, baseCost: 6435, interval: 50000, intervalCost: 1025 },
  { from: 500000, to: 1000000, baseCost: 14635, interval: 100000, intervalCost: 1985 },
  { from: 1000000, to: 5000000, baseCost: 24560, interval: 200000, intervalCost: 1985 },
  { from: 5000000, to: Infinity, baseCost: 64260, interval: 1000000, intervalCost: 5000 },
];

const transferDeedOfficeLeviesBrackets = [
  { from: 0, to: 200000, baseCost: 90 },
  { from: 200000, to: 300000, baseCost: 567 },
  { from: 300000, to: 600000, baseCost: 707 },
  { from: 600000, to: 800000, baseCost: 995 },
  { from: 800000, to: 1000000, baseCost: 1142 },
  { from: 1000000, to: 2000000, baseCost: 1283 },
  { from: 2000000, to: 4000000, baseCost: 1778 },
  { from: 4000000, to: 6000000, baseCost: 2157 },
  { from: 6000000, to: 8000000, baseCost: 2568 },
  { from: 8000000, to: 10000000, baseCost: 3002 },
  { from: 10000000, to: 15000000, baseCost: 3573 },
  { from: 15000000, to: 20000000, baseCost: 4292 },
  { from: 20000000, to: Infinity, baseCost: 5717 },
];

const bondDeedOfficeLeviesBrackets = [
  { from: 0, to: 200000, baseCost: 438 },
  { from: 200000, to: 300000, baseCost: 567 },
  { from: 300000, to: 600000, baseCost: 707 },
  { from: 600000, to: 800000, baseCost: 995 },
  { from: 800000, to: 1000000, baseCost: 1142 },
  { from: 1000000, to: 2000000, baseCost: 1283 },
  { from: 2000000, to: 4000000, baseCost: 1778 },
  { from: 4000000, to: 6000000, baseCost: 2157 },
  { from: 6000000, to: 8000000, baseCost: 2568 },
  { from: 8000000, to: 10000000, baseCost: 3002 },
  { from: 10000000, to: 15000000, baseCost: 3573 },
  { from: 15000000, to: 20000000, baseCost: 4292 },
  { from: 20000000, to: 30000000, baseCost: 5002 },
  { from: 30000000, to: Infinity, baseCost: 7147 },
];

function calculateFeeWithBaseAndIntervalCosts(amount: number, brackets: any[]) {
  const bracket = amount === 0 ? brackets[0] : brackets.find((x) => amount > x.from && amount <= x.to);

  if (!bracket) return 0;

  const { from, to, interval = to - from, intervalCost = 0, baseCost } = bracket;
  return Math.ceil((amount - from) / interval) * intervalCost + baseCost;
}

function getCountrySettings(countryCode?: string) {
  return {
    vatRate: 15,
    transferDuty: (price: number) => {
      if (price <= 1210000) return 0;
      if (price <= 1663800) return (price - 1210000) * 0.03;
      if (price <= 2329300) return 13614 + (price - 1663800) * 0.06;
      if (price <= 2994800) return 53544 + (price - 2329300) * 0.08;
      if (price <= 13310000) return 106784 + (price - 2994800) * 0.11;
      return 1241456 + (price - 13310000) * 0.13;
    },
    bondRegCost: (price: number) => calculateFeeWithBaseAndIntervalCosts(price, registrationFeeBrackets),
    transferCost: (price: number) => calculateFeeWithBaseAndIntervalCosts(price, registrationFeeBrackets),
    deedsOfficeFee: (price: number) => calculateFeeWithBaseAndIntervalCosts(price, transferDeedOfficeLeviesBrackets),
    bondDeedsOfficeFee: (price: number) => calculateFeeWithBaseAndIntervalCosts(price, bondDeedOfficeLeviesBrackets),
    otherFees: 1200,
    bankInitiationFee: (price: number) => {
      const baseFee = 1207.5;
      const amountAboveThreshold = Math.max(0, price - 50000);
      const percentageFee = amountAboveThreshold * 0.1;
      const totalFee = baseFee + percentageFee;
      return Math.min(totalFee, 6037.5);
    },
    // Additional fees
    transferDeedOfficeSearch: 100,
    transferPostage: 400,
    transferElectronicDocumentFee: 100,
    transferFica: 300,
    transferRatesClearanceCertificate: 250,
    transferProRateRates: 2000,
    bondDeedOfficeSearch: 100,
    bondPostage: 400,
    bondElectronicDocumentFee: 100,
    bondFica: 300,
    bondInsuranceCertificate: 400,
  };
}

export const TransferCostCalculator = ({ countryCode = "RSA", currencySymbol = "R" }: TransferCostCalculatorProps) => {
  const settings = getCountrySettings(countryCode);
  const [purchasePrice, setPurchasePrice] = useState("");
  const [loanAmount, setLoanAmount] = useState("");
  const [loanAmountTouched, setLoanAmountTouched] = useState(false);

  const handleNumberInput = (value: string, setter: (value: string) => void) => {
    // Only allow numbers and spaces
    const numericValue = value.replace(/[^0-9 ]/g, "");
    setter(numericValue);
  };

  // Sync loan amount to purchase price unless user has changed it manually
  if (!loanAmountTouched && purchasePrice !== loanAmount) {
    setLoanAmount(purchasePrice);
  }

  // --- Calculation logic ---
  const price = parseFloat(purchasePrice.replace(/ /g, "")) || 0;
  const loan = parseFloat(loanAmount.replace(/ /g, "")) || 0;

  // Bond registration costs
  const bondRegCost = loan > 0 ? settings.bondRegCost(loan) : 0;
  const bankInitiationFee = loan > 0 ? settings.bankInitiationFee(loan) : 0;
  const bondDeedsOfficeFee = loan > 0 ? settings.bondDeedsOfficeFee(loan) : 0;
  const bondDeedOfficeSearch = loan > 0 ? settings.bondDeedOfficeSearch : 0;
  const bondPostage = loan > 0 ? settings.bondPostage : 0;
  const bondElectronicDocumentFee = loan > 0 ? settings.bondElectronicDocumentFee : 0;
  const bondFica = loan > 0 ? settings.bondFica : 0;
  const bondInsuranceCertificate = loan > 0 ? settings.bondInsuranceCertificate : 0;

  // Transfer costs
  const transferCost = price > 0 ? settings.transferCost(price) : 0;
  const transferDuty = price > 0 ? settings.transferDuty(price) : 0;
  const transferDeedsOfficeFee = price > 0 ? settings.deedsOfficeFee(price) : 0;
  const transferDeedOfficeSearch = price > 0 ? settings.transferDeedOfficeSearch : 0;
  const transferPostage = price > 0 ? settings.transferPostage : 0;
  const transferElectronicDocumentFee = price > 0 ? settings.transferElectronicDocumentFee : 0;
  const transferFica = price > 0 ? settings.transferFica : 0;
  const transferRatesClearanceCertificate = price > 0 ? settings.transferRatesClearanceCertificate : 0;
  const transferProRateRates = price > 0 ? settings.transferProRateRates : 0;

  // Calculate VAT
  const bondVat = loan > 0 ? (bondRegCost + bondDeedOfficeSearch + bondPostage + bondElectronicDocumentFee + bondFica + bondInsuranceCertificate) * 0.15 : 0;

  const transferVat =
    price > 0
      ? (transferCost + transferDeedOfficeSearch + transferPostage + transferElectronicDocumentFee + transferFica + transferRatesClearanceCertificate) * 0.15
      : 0;

  const totalBondCosts =
    bondRegCost +
    bankInitiationFee +
    bondDeedsOfficeFee +
    bondDeedOfficeSearch +
    bondPostage +
    bondElectronicDocumentFee +
    bondFica +
    bondInsuranceCertificate +
    bondVat;

  const totalTransferCosts =
    transferCost +
    transferDuty +
    transferDeedsOfficeFee +
    transferDeedOfficeSearch +
    transferPostage +
    transferElectronicDocumentFee +
    transferFica +
    transferRatesClearanceCertificate +
    transferProRateRates +
    transferVat;

  const totalCosts = totalBondCosts + totalTransferCosts;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Transfer Cost Calculator</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="tw-flex tw-flex-col tw-gap-8 md:tw-flex-row">
          <div className="tw-w-full md:tw-w-1/2">
            <div className="tw-grid tw-gap-4">
              <div className="tw-space-y-2">
                <Label htmlFor="purchasePrice">
                  Purchase price (incl. VAT) <span className="tw-text-destructive">*</span>
                </Label>
                <Input
                  id="purchasePrice"
                  type="text"
                  value={formatInputNumber(purchasePrice)}
                  onChange={(e) => {
                    handleNumberInput(e.target.value, setPurchasePrice);
                    if (!loanAmountTouched) handleNumberInput(e.target.value, setLoanAmount);
                  }}
                  className="tw-w-full"
                  required
                />
              </div>
              <div className="tw-space-y-2">
                <Label htmlFor="loanAmount">
                  Loan amount <span className="tw-text-destructive">*</span>
                </Label>
                <Input
                  id="loanAmount"
                  type="text"
                  value={formatInputNumber(loanAmount)}
                  onChange={(e) => {
                    handleNumberInput(e.target.value, setLoanAmount);
                    setLoanAmountTouched(true);
                  }}
                  className="tw-w-full"
                  required
                />
              </div>
            </div>
          </div>
          <div className="tw-mt-8 tw-w-full md:tw-mt-0 md:tw-w-1/2">
            <div className="tw-space-y-6 tw-rounded-lg tw-bg-muted tw-p-4">
              <div className="tw-text-lg tw-font-semibold">
                Total costs:{" "}
                <span className="tw-text-primary">
                  {currencySymbol} {formatNumber(totalCosts, 0)}
                </span>
              </div>
              <div className="tw-mt-6 tw-space-y-2">
                <div className="tw-mb-1 tw-border-b tw-border-muted tw-text-base tw-font-medium">Transfer costs (incl VAT)</div>
                <div className="tw-flex tw-justify-between">
                  <span>Transfer duty</span>
                  <span>
                    {currencySymbol} {formatNumber(transferDuty, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>Transfer fees</span>
                  <span>
                    {currencySymbol} {formatNumber(transferCost, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>Deed Office Levies</span>
                  <span>
                    {currencySymbol} {formatNumber(transferDeedsOfficeFee, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>Deed Office Search</span>
                  <span>
                    {currencySymbol} {formatNumber(transferDeedOfficeSearch, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>Postage & Petties</span>
                  <span>
                    {currencySymbol} {formatNumber(transferPostage, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>Electronic Document Fee</span>
                  <span>
                    {currencySymbol} {formatNumber(transferElectronicDocumentFee, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>FICA</span>
                  <span>
                    {currencySymbol} {formatNumber(transferFica, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>Rates / Levies Clearance Certificate</span>
                  <span>
                    {currencySymbol} {formatNumber(transferRatesClearanceCertificate, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>Pro-Rate Rates / Levies</span>
                  <span>
                    {currencySymbol} {formatNumber(transferProRateRates, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>VAT</span>
                  <span>
                    {currencySymbol} {formatNumber(transferVat, 0)}
                  </span>
                </div>
                <div className="tw-mt-2 tw-flex tw-justify-between tw-font-semibold">
                  <span>Total transfer costs</span>
                  <span>
                    {currencySymbol} {formatNumber(totalTransferCosts, 0)}
                  </span>
                </div>
              </div>
              <div className="tw-mt-6 tw-space-y-2">
                <div className="tw-mb-1 tw-border-b tw-border-muted tw-text-base tw-font-medium">Bond costs</div>
                <div className="tw-flex tw-justify-between">
                  <span>Registration fees</span>
                  <span>
                    {currencySymbol} {formatNumber(bondRegCost, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>Deed Office Levies</span>
                  <span>
                    {currencySymbol} {formatNumber(bondDeedsOfficeFee, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>Deed Office Search</span>
                  <span>
                    {currencySymbol} {formatNumber(bondDeedOfficeSearch, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>Postage & Petties</span>
                  <span>
                    {currencySymbol} {formatNumber(bondPostage, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>Electronic Document Fee</span>
                  <span>
                    {currencySymbol} {formatNumber(bondElectronicDocumentFee, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>FICA</span>
                  <span>
                    {currencySymbol} {formatNumber(bondFica, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>Insurance Certificate</span>
                  <span>
                    {currencySymbol} {formatNumber(bondInsuranceCertificate, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>Bank Initiation Fee</span>
                  <span>
                    {currencySymbol} {formatNumber(bankInitiationFee, 0)}
                  </span>
                </div>
                <div className="tw-flex tw-justify-between">
                  <span>VAT</span>
                  <span>
                    {currencySymbol} {formatNumber(bondVat, 0)}
                  </span>
                </div>
                <div className="tw-mt-2 tw-flex tw-justify-between tw-font-semibold">
                  <span>Total bond costs</span>
                  <span>
                    {currencySymbol} {formatNumber(totalBondCosts, 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
